import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import VendorCard from '../components/VendorCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Search, SlidersHorizontal, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VendorsPage = () => {
  const [searchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  
  // Category-specific price ranges
  const getPriceRange = (cat) => {
    if (cat === 'photo-booth') {
      return { min: 0, max: 5000, step: 100 };
    }
    if (cat === 'entertainment') {
      return { min: 0, max: 10000, step: 250 };
    }
    return { min: 0, max: 50000, step: 1000 };
  };
  
  const priceConfig = getPriceRange(category);
  const [priceRange, setPriceRange] = useState([priceConfig.min, priceConfig.max]);
  
  // Update price range when category changes
  useEffect(() => {
    const newConfig = getPriceRange(category);
    setPriceRange([newConfig.min, newConfig.max]);
  }, [category]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vendors/categories`);
      setCategories(response.data.categories || []);
      setCities(response.data.cities || []);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategories();
  }, [fetchCategories]);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.set('category', category);
      if (city && city !== 'all') params.set('city', city);
      if (search) params.set('search', search);
      if (priceRange[0] > priceConfig.min) params.set('min_price', priceRange[0]);
      if (priceRange[1] < priceConfig.max) params.set('max_price', priceRange[1]);

      const response = await axios.get(`${API}/vendors?${params.toString()}`);
      const data = response.data;
      // Support both old array format and new {vendors, total} format
      if (Array.isArray(data)) {
        setVendors(data);
        setTotalCount(data.length);
      } else {
        setVendors(data.vendors || []);
        setTotalCount(data.total || 0);
      }
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [category, city, search, priceRange, priceConfig.min, priceConfig.max]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const clearFilters = () => {
    setCategory('');
    setCity('');
    setSearch('');
    const defaultConfig = getPriceRange('');
    setPriceRange([defaultConfig.min, defaultConfig.max]);
  };

  // Generate dynamic page title
  const getPageTitle = () => {
    const categoryName = categories.find(c => c.id === category)?.name;
    if (categoryName && city) {
      return `${categoryName} in ${city}`;
    } else if (categoryName) {
      return `${categoryName} - All Cities`;
    } else if (city) {
      return `Vendors in ${city}`;
    }
    return 'All Vendors';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-gray-900 mb-6">
            {getPageTitle()}
          </h1>
          
          {/* Filters Row */}
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search vendors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 rounded-xl border-gray-200"
                data-testid="vendor-search-input"
              />
            </div>

            {/* Category */}
            <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] h-11 rounded-xl border-gray-200" data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="rounded-xl z-50">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* City */}
            <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] h-11 rounded-xl border-gray-200" data-testid="filter-city">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent className="rounded-xl z-50 max-h-60">
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price Range */}
            <div className="hidden md:flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-xl">
              <span className="text-sm text-gray-500 whitespace-nowrap">
                ${priceRange[0].toLocaleString()} - ${priceRange[1] >= priceConfig.max ? `${priceRange[1].toLocaleString()}+` : priceRange[1].toLocaleString()}
              </span>
              <Slider
                value={priceRange}
                onValueChange={setPriceRange}
                max={priceConfig.max}
                min={priceConfig.min}
                step={priceConfig.step}
                className="w-32"
              />
            </div>

            {(category || city || search || priceRange[0] > priceConfig.min || priceRange[1] < priceConfig.max) && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="h-11 text-gray-500"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-500">
            {loading ? 'Loading...' : `${totalCount} vendors found`}
          </p>
          <Select defaultValue="rating">
            <SelectTrigger className="w-40 rounded-xl border-gray-200" data-testid="sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="rating">Top Rated</SelectItem>
              <SelectItem value="reviews">Most Reviews</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Vendor Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-gray-100" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-gray-100 rounded w-3/4" />
                  <div className="h-4 bg-gray-50 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="font-heading font-semibold text-xl mb-2">No vendors found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your filters</p>
            <Button onClick={clearFilters} className="rounded-xl">
              Clear Filters
            </Button>
          </div>
        ) : (
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.05 } }
            }}
          >
            {vendors.map((vendor) => (
              <VendorCard key={vendor.id} vendor={vendor} />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default VendorsPage;
